import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { PANEL_DEFINITION_MAP, PANEL_MENU_OPTIONS, PanelComponentId } from './panelRegistry';
import { PanelMenu } from './PanelMenu';
import { WorkspaceTabs } from './WorkspaceTabs';
import { useTheme, type ThemeId } from '../../state/themeStore';
import { PanelProvider } from './PanelContext';
import { bus } from '../../App';
import { useSelectionStore } from '../../state/selectionStore';
import {
  PanelTree,
  type PanelLeafNode,
  clonePanelTree,
  countLeaves,
  isLeaf,
  removePanel,
  replaceLeafComponent,
  splitPanel,
  swapLeafContent,
  updatePanel,
} from './panelTree';

const LEGACY_LAYOUT_STORAGE_KEY = 'dashboard:panel-layout:v1';
const WORKSPACES_STORAGE_KEY = 'dashboard:panel-workspaces:v1';
const MIN_PANEL_SHARE = 5;

type EdgeDirection = 'left' | 'right' | 'top' | 'bottom';
interface EdgeDragRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

interface EdgeDragState {
  direction: EdgeDirection;
  pointerId: number;
  rect: EdgeDragRect;
  share: number;
}

interface DragState {
  panelId: string;
  overPanelId?: string;
}

const DEFAULT_EDGE_PANEL_COMPONENT: PanelComponentId = 'app';
const EDGE_INSERT_THRESHOLD = MIN_PANEL_SHARE;

function clampEdgeShare(value: number): number {
  if (!Number.isFinite(value)) {
    return EDGE_INSERT_THRESHOLD;
  }
  const bounded = Math.max(0, Math.min(100, value));
  const min = EDGE_INSERT_THRESHOLD;
  const max = 100 - EDGE_INSERT_THRESHOLD;
  const clamped = Math.min(max, Math.max(min, bounded));
  return Math.round(clamped * 100) / 100;
}

function computeEdgeShare(direction: EdgeDirection, clientX: number, clientY: number, rect: EdgeDragRect): number {
  if (rect.width <= 0 || rect.height <= 0) {
    return 0;
  }
  let percentage = 0;
  switch (direction) {
    case 'left':
      percentage = ((clientX - rect.left) / rect.width) * 100;
      break;
    case 'right':
      percentage = ((rect.right - clientX) / rect.width) * 100;
      break;
    case 'top':
      percentage = ((clientY - rect.top) / rect.height) * 100;
      break;
    case 'bottom':
      percentage = ((rect.bottom - clientY) / rect.height) * 100;
      break;
    default:
      percentage = 0;
      break;
  }
  if (!Number.isFinite(percentage)) {
    return 0;
  }
  return Math.max(0, Math.min(100, percentage));
}

function cloneWithSize(node: PanelTree, size: number): PanelTree {
  const cloned = clonePanelTree(node);
  return { ...cloned, size };
}

function insertPanelAtEdge(
  baseLayout: PanelTree,
  direction: EdgeDirection,
  share: number,
  splitId: string,
  newLeafId: string,
  componentId: PanelComponentId,
): PanelTree {
  const normalizedShare = clampEdgeShare(share);
  const axis: 'horizontal' | 'vertical' = direction === 'left' || direction === 'right' ? 'horizontal' : 'vertical';
  const newLeaf: PanelLeafNode = {
    id: newLeafId,
    type: 'leaf',
    componentId,
    size: normalizedShare,
  };
  const existingShare = 100 - normalizedShare;
  const existing = cloneWithSize(baseLayout, existingShare);
  const children =
    direction === 'left' || direction === 'top'
      ? [newLeaf, existing]
      : [existing, newLeaf];
  return {
    id: splitId,
    type: 'split',
    direction: axis,
    size: 100,
    children: children as [PanelTree, PanelTree],
  };
}

interface WorkspaceState {
  id: string;
  name: string;
  layout: PanelTree;
}

interface StoredWorkspaceState {
  activeId: string;
  workspaces: WorkspaceState[];
}

function isPanelTreeCandidate(node: unknown): node is PanelTree {
  if (!node || typeof node !== 'object') {
    return false;
  }
  const base = node as {
    id?: unknown;
    type?: unknown;
    direction?: unknown;
    children?: unknown;
    componentId?: unknown;
    size?: unknown;
  };
  if (typeof base.id !== 'string') {
    return false;
  }
  if (base.size !== undefined && (typeof base.size !== 'number' || !Number.isFinite(base.size))) {
    return false;
  }
  if (base.type === 'leaf') {
    if (typeof base.componentId !== 'string') {
      return false;
    }
    return Object.prototype.hasOwnProperty.call(PANEL_DEFINITION_MAP, base.componentId);
  }
  if (base.type === 'split') {
    if (base.direction !== 'horizontal' && base.direction !== 'vertical') {
      return false;
    }
    if (!Array.isArray(base.children) || base.children.length !== 2) {
      return false;
    }
    return isPanelTreeCandidate(base.children[0]) && isPanelTreeCandidate(base.children[1]);
  }
  return false;
}

function isWorkspaceCandidate(value: unknown): value is WorkspaceState {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const base = value as {
    id?: unknown;
    name?: unknown;
    layout?: unknown;
  };
  if (typeof base.id !== 'string' || typeof base.name !== 'string') {
    return false;
  }
  return isPanelTreeCandidate(base.layout);
}

function isWorkspaceStateCandidate(value: unknown): value is StoredWorkspaceState {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const base = value as {
    activeId?: unknown;
    workspaces?: unknown;
  };
  if (typeof base.activeId !== 'string' || !Array.isArray(base.workspaces)) {
    return false;
  }
  return base.workspaces.every(isWorkspaceCandidate);
}

function loadLegacyLayout(): PanelTree | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(LEGACY_LAYOUT_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    if (!isPanelTreeCandidate(parsed)) {
      return null;
    }
    return clonePanelTree(parsed);
  } catch (error) {
    console.warn('[PanelWorkspace] Failed to load legacy layout from localStorage', error);
    return null;
  }
}

function loadStoredWorkspaceState(): StoredWorkspaceState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(WORKSPACES_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    if (!isWorkspaceStateCandidate(parsed)) {
      return null;
    }
    return {
      activeId: parsed.activeId,
      workspaces: parsed.workspaces.map((workspace) => ({
        ...workspace,
        layout: clonePanelTree(workspace.layout),
      })),
    };
  } catch (error) {
    console.warn('[PanelWorkspace] Failed to load workspace state from localStorage', error);
    return null;
  }
}

function findMaxGeneratedId(node: PanelTree, currentMax = 100): number {
  const match = /^panel-(\d+)$/.exec(node.id);
  let maxId = currentMax;
  if (match) {
    maxId = Math.max(maxId, Number.parseInt(match[1], 10));
  }
  if (node.type === 'split') {
    maxId = findMaxGeneratedId(node.children[0], maxId);
    maxId = findMaxGeneratedId(node.children[1], maxId);
  }
  return maxId;
}

function findMaxWorkspaceId(workspaces: WorkspaceState[], currentMax = 1): number {
  return workspaces.reduce((max, workspace) => {
    const match = /^workspace-(\d+)$/.exec(workspace.id);
    if (match) {
      return Math.max(max, Number.parseInt(match[1], 10));
    }
    return max;
  }, currentMax);
}

function findMaxPanelIdAcrossWorkspaces(workspaces: WorkspaceState[], currentMax = 100): number {
  return workspaces.reduce((max, workspace) => findMaxGeneratedId(workspace.layout, max), currentMax);
}

function clampSize(value: number | undefined, fallback = 50): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback;
  }
  return Math.max(MIN_PANEL_SHARE, Math.min(100 - MIN_PANEL_SHARE, value));
}

function normalizePair([a, b]: [number, number]): [number, number] {
  const total = a + b;
  if (!Number.isFinite(total) || total <= 0) {
    return [50, 50];
  }
  return [
    (a / total) * 100,
    (b / total) * 100,
  ];
}

const BASE_LAYOUT: PanelTree = {
  id: 'root',
  type: 'split',
  direction: 'horizontal',
  size: 100,
  children: [
    { id: 'sessions', type: 'leaf', componentId: 'sessions', size: 33.33 },
    {
      id: 'center-right',
      type: 'split',
      direction: 'horizontal',
      size: 66.67,
      children: [
        { id: 'messages', type: 'leaf', componentId: 'messages', size: 50 },
        { id: 'details', type: 'leaf', componentId: 'messageDetail', size: 50 },
      ],
    },
  ],
};

function createDefaultLayout(): PanelTree {
  return clonePanelTree(BASE_LAYOUT);
}

function createWorkspace(id: string, name: string, layout?: PanelTree): WorkspaceState {
  return {
    id,
    name,
    layout: layout ? clonePanelTree(layout) : createDefaultLayout(),
  };
}

export function PanelWorkspace() {
  const initialState = useMemo(() => {
    const stored = loadStoredWorkspaceState();
    if (stored) {
      return stored;
    }
    const legacyLayout = loadLegacyLayout();
    if (legacyLayout) {
      return {
        activeId: 'workspace-1',
        workspaces: [createWorkspace('workspace-1', 'Workspace 1', legacyLayout)],
      } satisfies StoredWorkspaceState;
    }
    return {
      activeId: 'workspace-1',
      workspaces: [createWorkspace('workspace-1', 'Workspace 1')],
    } satisfies StoredWorkspaceState;
  }, []);

  const [workspaces, setWorkspaces] = useState<WorkspaceState[]>(initialState.workspaces);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(initialState.activeId);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [edgeDragState, setEdgeDragState] = useState<EdgeDragState | null>(null);
  const [focusedPanelId, setFocusedPanelId] = useState<string | null>(null);
  const tracking = useSelectionStore((state) => state.tracking);
  const select = useSelectionStore((state) => state.set);

  const { themeId, resolvedThemeId, availableThemes, setTheme } = useTheme();
  const [panelTitles, setPanelTitles] = useState<Record<string, string>>({});

  const setPanelTitle = useCallback((panelId: string, title: string) => {
    setPanelTitles((prev) => ({ ...prev, [panelId]: title }));
  }, []);

  const panelIdCounter = useRef(findMaxPanelIdAcrossWorkspaces(initialState.workspaces));
  const workspaceIdCounter = useRef(findMaxWorkspaceId(initialState.workspaces));
  const edgeDragStateRef = useRef<EdgeDragState | null>(null);
  const workspaceContentRef = useRef<HTMLDivElement | null>(null);
  const edgeDragHandleRef = useRef<HTMLDivElement | null>(null);

  const nextPanelId = useCallback(() => {
    panelIdCounter.current += 1;
    return `panel-${panelIdCounter.current}`;
  }, []);

  const nextWorkspaceId = useCallback(() => {
    workspaceIdCounter.current += 1;
    return `workspace-${workspaceIdCounter.current}`;
  }, []);

  const activeWorkspace = useMemo(() => {
    if (workspaces.length === 0) {
      return null;
    }
    return workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? workspaces[0];
  }, [activeWorkspaceId, workspaces]);

  useEffect(() => {
    if (activeWorkspace && activeWorkspace.id !== activeWorkspaceId) {
      setActiveWorkspaceId(activeWorkspace.id);
    }
  }, [activeWorkspace, activeWorkspaceId]);

  // Handle tracking of new messages
  useEffect(() => {
    if (!tracking) {
      return;
    }

    const unsubscribe = bus.on('message/created', (_event, payload) => {
      if (payload && typeof payload === 'object') {
        const { session_id, message_id } = payload as { session_id?: unknown; message_id?: unknown };
        if (typeof session_id === 'number') select({ session_id });
        if (typeof message_id === 'number') select({ message_id });
      }
    });

    return unsubscribe;
  }, [tracking, select]);

  const activeLeafCount = useMemo(() => (activeWorkspace ? countLeaves(activeWorkspace.layout) : 0), [activeWorkspace]);

  const updateWorkspaceLayout = useCallback((workspaceId: string, updater: (layout: PanelTree) => PanelTree) => {
    setWorkspaces((prev) =>
      prev.map((workspace) =>
        workspace.id === workspaceId ? { ...workspace, layout: updater(workspace.layout) } : workspace,
      ),
    );
  }, []);

  const handleChangeComponent = useCallback(
    (panelId: string, component: PanelComponentId) => {
      if (!activeWorkspace) {
        return;
      }
      updateWorkspaceLayout(activeWorkspace.id, (layout) => replaceLeafComponent(layout, panelId, component));
    },
    [activeWorkspace, updateWorkspaceLayout],
  );

  const handleSplit = useCallback(
    (panelId: string, direction: 'horizontal' | 'vertical') => {
      if (!activeWorkspace) {
        return;
      }
      let createdPanelId: string | null = null;
      updateWorkspaceLayout(activeWorkspace.id, (layout) =>
        splitPanel(layout, panelId, direction, () => {
          const id = nextPanelId();
          createdPanelId = id;
          return id;
        }),
      );
      if (createdPanelId) {
        setFocusedPanelId(createdPanelId);
      }
    },
    [activeWorkspace, nextPanelId, updateWorkspaceLayout],
  );

  const handleResize = useCallback(
    (splitId: string, sizes: number[]) => {
      if (!activeWorkspace) {
        return;
      }
      const normalized = normalizePair([
        clampSize(sizes[0]),
        clampSize(sizes[1]),
      ]);
      updateWorkspaceLayout(activeWorkspace.id, (layout) =>
        updatePanel(layout, splitId, (node) => {
          if (node.type !== 'split') {
            return node;
          }
          const [first, second] = node.children;
          return {
            ...node,
            children: [
              { ...first, size: normalized[0] },
              { ...second, size: normalized[1] },
            ] as [PanelTree, PanelTree],
          };
        }),
      );
    },
    [activeWorkspace, updateWorkspaceLayout],
  );

  const handleClose = useCallback(
    (panelId: string) => {
      if (!activeWorkspace || activeLeafCount <= 1) {
        return;
      }
      updateWorkspaceLayout(activeWorkspace.id, (layout) => removePanel(layout, panelId));
    },
    [activeLeafCount, activeWorkspace, updateWorkspaceLayout],
  );

  const resetLayout = useCallback(() => {
    if (!activeWorkspace) {
      return;
    }
    panelIdCounter.current = findMaxGeneratedId(BASE_LAYOUT);
    updateWorkspaceLayout(activeWorkspace.id, () => createDefaultLayout());
  }, [activeWorkspace, updateWorkspaceLayout]);

  const handleAddWorkspace = useCallback(() => {
    const newId = nextWorkspaceId();
    const newName = `Workspace ${workspaceIdCounter.current}`;
    setWorkspaces((prev) => [...prev, createWorkspace(newId, newName)]);
    setActiveWorkspaceId(newId);
  }, [nextWorkspaceId]);

  const handleRenameWorkspace = useCallback((workspaceId: string, name: string) => {
    setWorkspaces((prev) =>
      prev.map((workspace) => (workspace.id === workspaceId ? { ...workspace, name } : workspace)),
    );
  }, []);

  const handleRemoveWorkspace = useCallback(
    (workspaceId: string) => {
      setWorkspaces((prev) => {
        if (prev.length <= 1) {
          return prev;
        }
        const next = prev.filter((workspace) => workspace.id !== workspaceId);
        if (next.length === prev.length) {
          return prev;
        }
        if (workspaceId === activeWorkspaceId && next.length > 0) {
          setActiveWorkspaceId(next[0].id);
        }
        return next;
      });
    },
    [activeWorkspaceId],
  );

  const handlePanelDragStart = useCallback((event: DragEvent<HTMLElement>, panelId: string) => {
    event.stopPropagation();
    event.dataTransfer.setData('text/plain', panelId);
    event.dataTransfer.effectAllowed = 'move';
    setDragState({ panelId });
  }, []);

  const handlePanelDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  const handlePanelDragEnter = useCallback(
    (event: DragEvent<HTMLElement>, panelId: string) => {
      event.preventDefault();
      event.stopPropagation();
      setDragState((prev) => {
        if (!prev) {
          return prev;
        }
        if (panelId === prev.panelId) {
          if (prev.overPanelId === undefined) {
            return prev;
          }
          return { ...prev, overPanelId: undefined };
        }
        if (prev.overPanelId === panelId) {
          return prev;
        }
        return { ...prev, overPanelId: panelId };
      });
    },
    [],
  );

  const handlePanelDragOver = useCallback(
    (event: DragEvent<HTMLElement>, panelId: string) => {
      if (!dragState) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = panelId === dragState.panelId ? 'none' : 'move';
      setDragState((prev) => {
        if (!prev) {
          return prev;
        }
        if (panelId === prev.panelId) {
          if (prev.overPanelId === undefined) {
            return prev;
          }
          return { ...prev, overPanelId: undefined };
        }
        if (prev.overPanelId === panelId) {
          return prev;
        }
        return { ...prev, overPanelId: panelId };
      });
    },
    [dragState],
  );

  const handlePanelDrop = useCallback(
    (event: DragEvent<HTMLElement>, panelId: string) => {
      event.preventDefault();
      event.stopPropagation();
      const currentDragState = dragState;
      const sourceId = currentDragState?.panelId;
      setDragState(null);
      if (!activeWorkspace || !sourceId || sourceId === panelId) {
        return;
      }
      updateWorkspaceLayout(activeWorkspace.id, (layout) => swapLeafContent(layout, sourceId, panelId));
      setFocusedPanelId(sourceId);
    },
    [activeWorkspace, dragState, updateWorkspaceLayout],
  );

  const clearDragState = useCallback(() => {
    setDragState(null);
  }, []);

  const updateEdgeDragShare = useCallback((share: number) => {
    const current = edgeDragStateRef.current;
    if (!current) {
      return;
    }
    const bounded = Math.max(0, Math.min(100, share));
    if (bounded === current.share) {
      return;
    }
    const nextState: EdgeDragState = { ...current, share: bounded };
    edgeDragStateRef.current = nextState;
    setEdgeDragState(nextState);
  }, []);

  const finalizeEdgeDrag = useCallback(
    (state: EdgeDragState | null, cancelled: boolean) => {
      if (!state) {
        return;
      }
      const rawShare = Math.max(0, Math.min(100, state.share));
      const remainingRaw = 100 - rawShare;
      if (!cancelled && rawShare >= EDGE_INSERT_THRESHOLD && remainingRaw >= EDGE_INSERT_THRESHOLD && activeWorkspace) {
        const share = clampEdgeShare(rawShare);
        const newSplitId = nextPanelId();
        const newLeafId = nextPanelId();
        updateWorkspaceLayout(activeWorkspace.id, (layout) =>
          insertPanelAtEdge(layout, state.direction, share, newSplitId, newLeafId, DEFAULT_EDGE_PANEL_COMPONENT),
        );
        setFocusedPanelId(newLeafId);
      }
      if (edgeDragHandleRef.current) {
        try {
          edgeDragHandleRef.current.releasePointerCapture(state.pointerId);
        } catch {
          // Ignore if capture was already released.
        }
      }
      edgeDragHandleRef.current = null;
      edgeDragStateRef.current = null;
      setEdgeDragState(null);
    },
    [activeWorkspace, nextPanelId, updateWorkspaceLayout],
  );

  useEffect(() => {
    edgeDragStateRef.current = edgeDragState;
  }, [edgeDragState]);

  useEffect(() => {
    if (!edgeDragState) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const current = edgeDragStateRef.current;
      if (!current || event.pointerId !== current.pointerId) {
        return;
      }
      event.preventDefault();
      const nextShare = computeEdgeShare(current.direction, event.clientX, event.clientY, current.rect);
      updateEdgeDragShare(nextShare);
    };

    const handlePointerEnd = (event: PointerEvent) => {
      const current = edgeDragStateRef.current;
      if (!current || event.pointerId !== current.pointerId) {
        return;
      }
      event.preventDefault();
      finalizeEdgeDrag(current, event.type === 'pointercancel');
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [edgeDragState, finalizeEdgeDrag, updateEdgeDragShare]);

  const startEdgeDrag = useCallback(
    (direction: EdgeDirection, event: ReactPointerEvent<HTMLDivElement>) => {
      if (!activeWorkspace || event.button !== 0) {
        return;
      }
      const container = workspaceContentRef.current;
      if (!container) {
        return;
      }
      const rect = container.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rectSnapshot: EdgeDragRect = {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      };
      const initialShare = computeEdgeShare(direction, event.clientX, event.clientY, rectSnapshot);
      const nextState: EdgeDragState = {
        direction,
        pointerId: event.pointerId,
        rect: rectSnapshot,
        share: initialShare,
      };
      if (event.currentTarget.setPointerCapture) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Ignore capture errors (e.g., already captured).
        }
      }
      edgeDragHandleRef.current = event.currentTarget;
      edgeDragStateRef.current = nextState;
      setEdgeDragState(nextState);
      updateEdgeDragShare(initialShare);
    },
    [activeWorkspace, updateEdgeDragShare],
  );

  const continueEdgeDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const current = edgeDragStateRef.current;
      if (!current || event.pointerId !== current.pointerId) {
        return;
      }
      event.preventDefault();
      const nextShare = computeEdgeShare(current.direction, event.clientX, event.clientY, current.rect);
      updateEdgeDragShare(nextShare);
    },
    [updateEdgeDragShare],
  );

  const endEdgeDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const current = edgeDragStateRef.current;
      if (!current || event.pointerId !== current.pointerId) {
        return;
      }
      event.preventDefault();
      finalizeEdgeDrag(current, false);
    },
    [finalizeEdgeDrag],
  );

  const cancelEdgeDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const current = edgeDragStateRef.current;
      if (!current || event.pointerId !== current.pointerId) {
        return;
      }
      event.preventDefault();
      finalizeEdgeDrag(current, true);
    },
    [finalizeEdgeDrag],
  );
  const renderNode = useCallback(
    (node: PanelTree): JSX.Element => {
      if (isLeaf(node)) {
        const panelDefinition = PANEL_DEFINITION_MAP[node.componentId];
        const PanelComponent = panelDefinition.component;
        const isDragSource = dragState?.panelId === node.id;
        const isDropTarget = Boolean(dragState && dragState.overPanelId === node.id && dragState.panelId !== node.id);
        const isFocused = focusedPanelId === node.id;
        const leafClassName = [
          'panel-wrapper',
          isDragSource ? 'is-drag-source' : '',
          isDropTarget ? 'is-drop-target' : '',
          isFocused ? 'is-focused' : '',
        ]
          .filter(Boolean)
          .join(' ');
        const headerClassName = `panel-header-bar${isDragSource ? ' is-dragging' : ''}`;
        return (
          <div
            className={leafClassName}
            onDragEnter={(event) => handlePanelDragEnter(event, node.id)}
            onDragOver={(event) => handlePanelDragOver(event, node.id)}
            onDrop={(event) => handlePanelDrop(event, node.id)}
            onClick={() => setFocusedPanelId(node.id)}
          >
            <div
              className={headerClassName}
              draggable
              onDragStart={(event) => handlePanelDragStart(event, node.id)}
              onDragEnd={handlePanelDragEnd}
              onClick={(event) => event.preventDefault()}
            >
              <select
                value={node.componentId}
                onChange={(event) => handleChangeComponent(node.id, event.target.value as PanelComponentId)}
                className="input-surface"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                {PANEL_MENU_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="panel-menu-anchor">
                <PanelMenu
                  panelId={node.id}
                  componentId={node.componentId}
                  title={panelTitles[node.id]}
                  onChange={handleChangeComponent}
                  onSplitHorizontal={(id) => handleSplit(id, 'horizontal')}
                  onSplitVertical={(id) => handleSplit(id, 'vertical')}
                  onClose={handleClose}
                  canClose={activeLeafCount > 1}
                />
              </div>
            </div>
            <div className="panel-content-wrapper">
              <PanelProvider
                panelId={node.id}
                isFocused={isFocused}
                title={panelTitles[node.id]}
                setTitle={setPanelTitle}
              >
                <PanelComponent />
              </PanelProvider>
            </div>
            {isDropTarget && (
              <div className="panel-drop-overlay" aria-hidden="true">
                <div className="panel-drop-preview panel-drop-preview--center">
                  <div className="panel-drop-preview__pane panel-drop-preview__pane--full" />
                </div>
              </div>
            )}
          </div>
        );
      }

      const [firstChild, secondChild] = node.children;
      const layoutSizes = normalizePair([
        clampSize(firstChild.size),
        clampSize(secondChild.size),
      ]);

      return (
        <PanelGroup
          key={node.id}
          direction={node.direction}
          className="workspace-content"
          layout={layoutSizes}
          onLayout={(sizes) => handleResize(node.id, sizes)}
        >
          <Panel key={`${node.id}-a`} minSize={MIN_PANEL_SHARE} defaultSize={layoutSizes[0]} className="workspace-content">
            {renderNode(firstChild)}
          </Panel>
          <PanelResizeHandle className="panel-resize-handle" />
          <Panel key={`${node.id}-b`} minSize={MIN_PANEL_SHARE} defaultSize={layoutSizes[1]} className="workspace-content">
            {renderNode(secondChild)}
          </Panel>
        </PanelGroup>
      );
    },
    [
      activeLeafCount,
      dragState,
      focusedPanelId,
      panelTitles,
      setPanelTitle,
      handleChangeComponent,
      handleClose,
      handlePanelDragEnd,
      handlePanelDragEnter,
      handlePanelDragOver,
      handlePanelDragStart,
      handlePanelDrop,
      handleResize,
      handleSplit,
    ],
  );

  const content = useMemo(() => (activeWorkspace ? renderNode(activeWorkspace.layout) : null), [activeWorkspace, renderNode]);

  const edgePreview = useMemo(() => {
    if (!edgeDragState) {
      return null;
    }
    const share = Math.max(0, Math.min(100, edgeDragState.share));
    const eligible = share >= EDGE_INSERT_THRESHOLD && 100 - share >= EDGE_INSERT_THRESHOLD;
    return {
      direction: edgeDragState.direction,
      share,
      eligible,
    };
  }, [edgeDragState]);

  const edgePreviewOverlay = useMemo(() => {
    if (!edgePreview) {
      return null;
    }
    const sizeAdjustment = 'calc(var(--panel-gap) * 2)';
    const sizeStyle: CSSProperties =
      edgePreview.direction === 'left' || edgePreview.direction === 'right'
        ? { width: `max(0px, calc(${edgePreview.share}% - ${sizeAdjustment}))` }
        : { height: `max(0px, calc(${edgePreview.share}% - ${sizeAdjustment}))` };
    return (
      <div
        className={`workspace-edge-preview workspace-edge-preview--${edgePreview.direction}`}
        data-eligible={edgePreview.eligible}
        style={sizeStyle}
        aria-hidden="true"
      >
        <div className="workspace-edge-placeholder">
          <span>Add panel</span>
        </div>
      </div>
    );
  }, [edgePreview]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const snapshot: StoredWorkspaceState = {
          activeId: activeWorkspaceId,
          workspaces: workspaces.map((workspace) => ({
            ...workspace,
            layout: workspace.layout,
          })),
        };
        window.localStorage.setItem(WORKSPACES_STORAGE_KEY, JSON.stringify(snapshot));
        window.localStorage.removeItem(LEGACY_LAYOUT_STORAGE_KEY);
      } catch (error) {
        console.warn('[PanelWorkspace] Failed to persist workspace state to localStorage', error);
      }
    }
    panelIdCounter.current = findMaxPanelIdAcrossWorkspaces(workspaces, panelIdCounter.current);
    workspaceIdCounter.current = findMaxWorkspaceId(workspaces, workspaceIdCounter.current);
  }, [activeWorkspaceId, workspaces]);

  if (!activeWorkspace) {
    return null;
  }

  return (
    <div
      className="workspace-background"
      onDrop={(event) => {
        event.preventDefault();
        clearDragState();
      }}
      onDragOver={(event) => event.preventDefault()}
    >
      <div className="workspace-top-bar">
        <WorkspaceTabs
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspace.id}
          onSelect={setActiveWorkspaceId}
          onAdd={handleAddWorkspace}
          onRename={handleRenameWorkspace}
          onRemove={handleRemoveWorkspace}
          canRemoveWorkspace
        />
        <div className="workspace-controls">
          <button
            type="button"
            onClick={() => select({ tracking: !tracking })}
            className="btn btn--toggle"
            data-active={tracking}
            aria-label={tracking ? 'Disable message tracking' : 'Enable message tracking'}
          >
            <span className="badge-dot" />
            Tracking
          </button>
          <button
            type="button"
            onClick={resetLayout}
            className="btn"
          >
            Reset layout
          </button>
          <div className="theme-selector">
            <span>Theme</span>
            <select
              className="input-surface"
              value={themeId}
              onChange={(event) => setTheme(event.target.value as ThemeId)}
              aria-label="Select theme"
            >
              {availableThemes.map((theme) => (
                <option key={theme.id} value={theme.id}>
                  {theme.label}
                  {theme.id !== 'auto' && theme.id === resolvedThemeId ? ' ✓' : ''}
                </option>
              ))}
            </select>
          </div>
          {activeLeafCount <= 1 && (
            <span className="workspace-message">
              Keep at least one panel open
            </span>
          )}
        </div>
      </div>
      <div
        className="workspace-content workspace-wrapper"
        ref={workspaceContentRef}
        data-edge-dragging={Boolean(edgeDragState)}
      >
        {content}
        {edgePreviewOverlay}
        <div
          className="workspace-edge-handle workspace-edge-handle--left"
          role="separator"
          aria-orientation="vertical"
          aria-label="Add panel on left edge"
          onPointerDown={(event) => startEdgeDrag('left', event)}
          onPointerMove={continueEdgeDrag}
          onPointerUp={endEdgeDrag}
          onPointerCancel={cancelEdgeDrag}
        />
        <div
          className="workspace-edge-handle workspace-edge-handle--right"
          role="separator"
          aria-orientation="vertical"
          aria-label="Add panel on right edge"
          onPointerDown={(event) => startEdgeDrag('right', event)}
          onPointerMove={continueEdgeDrag}
          onPointerUp={endEdgeDrag}
          onPointerCancel={cancelEdgeDrag}
        />
        <div
          className="workspace-edge-handle workspace-edge-handle--top"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Add panel on top edge"
          onPointerDown={(event) => startEdgeDrag('top', event)}
          onPointerMove={continueEdgeDrag}
          onPointerUp={endEdgeDrag}
          onPointerCancel={cancelEdgeDrag}
        />
        <div
          className="workspace-edge-handle workspace-edge-handle--bottom"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Add panel on bottom edge"
          onPointerDown={(event) => startEdgeDrag('bottom', event)}
          onPointerMove={continueEdgeDrag}
          onPointerUp={endEdgeDrag}
          onPointerCancel={cancelEdgeDrag}
        />
      </div>
    </div>
  );
}
